import { Icon } from '@buggy/shared'
import { skipToken } from '@reduxjs/toolkit/query'
import { Flex, Tooltip, Skeleton } from 'antd'
import isUndefined from 'lodash-es/isUndefined'
import React, { FunctionComponent, useMemo } from 'react'
import { useWindowSize } from 'react-use'
import styled from 'styled-components'
import { Duration } from '@/components/common/duration-select'
import { levelMap } from '@/components/common/k-score/get-level'
import KolDetailDataBlock from '@/components/kol/common/kol-detail-data-block'
import MetricChart from '@/components/kol/detail/overview/platform-metrics-summary/metric-chart'
import useKolMetricPerformanceSummary from '@/components/kol/hooks/use-kol-metric-performance-summary'
import { mockKolInfoKpi } from '@/constants/kol-detail/info-kpi'
import { mockSummary } from '@/constants/kol-detail/performance-summary'
import { type KolDetailDuration } from '@/hooks/use-authorization/routes'
import { useIntl } from '@/i18n/hooks/use-intl'
import type { KolSchemaModel } from '@/types/schema/kol-schema'
import { type PerformanceQuartileMetrics } from '@/types/schema/kpi-schema'
import { type Platform, PlatformShortcode } from '@/utils/convert-platform'

interface BasicInfoProps {
  kolInfo?: KolSchemaModel
  platform: Platform
  isKolInfoLocked: boolean
  isFetchingKolInfo: boolean
  showMockData?: boolean
  duration: KolDetailDuration
}

const PlatformMetricsSummary: FunctionComponent<BasicInfoProps> = ({
  kolInfo,
  platform,
  isKolInfoLocked,
  isFetchingKolInfo,
  showMockData,
  duration,
}) => {
  const { formatMessage } = useIntl()
  const hiddenData = isKolInfoLocked && !showMockData

  const { performanceSummary, isFetchingPerformanceSummary } =
    useKolMetricPerformanceSummary(hiddenData ? skipToken : undefined)

  const kpi = useMemo(
    () => kolInfo?.kpi.find((kpi) => kpi.platform === platform.shortcode),
    [kolInfo, platform],
  )
  const fansUpRate = useMemo((): number | undefined => {
    if (isKolInfoLocked) {
      return mockKolInfoKpi.fansUpRate3m
    }

    return duration === Duration.ThreeMonths
      ? kpi?.fansUpRate3m
      : kpi?.fansUpRate6m
  }, [isKolInfoLocked, duration, kpi])

  const { interactiveRate, avgShortPlayRate, avgPvRate } = useMemo(() => {
    if (isUndefined(kpi)) {
      return {
        interactiveRate: undefined,
        avgShortPlayRate: null,
        avgPvRate: null,
      }
    }
    if (duration === Duration.ThreeMonths) {
      return {
        interactiveRate: kpi.interactiveRate3m,
        avgShortPlayRate: kpi.avgShortPlayRate3m,
        avgPvRate: kpi.avgPvRate3m,
      }
    }

    return {
      interactiveRate: kpi.interactiveRate6m,
      avgShortPlayRate: kpi.avgShortPlayRate6m,
      avgPvRate: kpi.avgPvRate6m,
    }
  }, [kpi, duration])

  const viewRate = useMemo(() => {
    if (
      [PlatformShortcode.Instagram, PlatformShortcode.TikTok].includes(
        platform.shortcode,
      )
    ) {
      return avgShortPlayRate
    }

    return avgPvRate
  }, [avgPvRate, avgShortPlayRate, platform.shortcode])

  const {
    interactiveRatePerformance,
    viewRatePerformance,
    fansUpRatePerformance,
  }: {
    interactiveRatePerformance: PerformanceQuartileMetrics | undefined
    viewRatePerformance: PerformanceQuartileMetrics | undefined
    fansUpRatePerformance: PerformanceQuartileMetrics | undefined
  } = useMemo(() => {
    if (isUndefined(kpi)) {
      return {
        interactiveRatePerformance: undefined,
        viewRatePerformance: undefined,
        fansUpRatePerformance: undefined,
      }
    }

    if (isKolInfoLocked) {
      return {
        interactiveRatePerformance: mockKolInfoKpi.interactiveRatePerformance3m,
        viewRatePerformance: mockKolInfoKpi.avgPvRatePerformance3m,
        fansUpRatePerformance: mockKolInfoKpi.fansUpRatePerformance3m,
      }
    }

    if (duration === Duration.ThreeMonths) {
      return {
        interactiveRatePerformance: kpi.interactiveRatePerformance3m,
        viewRatePerformance: [
          PlatformShortcode.Instagram,
          PlatformShortcode.TikTok,
        ].includes(platform.shortcode)
          ? kpi.avgShortViewRatePerformance3m
          : kpi.avgPvRatePerformance3m,
        fansUpRatePerformance: kpi.fansUpRatePerformance3m,
      }
    }

    return {
      interactiveRatePerformance: kpi.interactiveRatePerformance6m,
      viewRatePerformance: [
        PlatformShortcode.Instagram,
        PlatformShortcode.TikTok,
      ].includes(platform.shortcode)
        ? kpi.avgShortViewRatePerformance6m
        : kpi.avgPvRatePerformance6m,
      fansUpRatePerformance: kpi.fansUpRatePerformance6m,
    }
  }, [duration, isKolInfoLocked, kpi, platform.shortcode])

  const summary = useMemo(
    () => (isKolInfoLocked ? mockSummary.summary : performanceSummary?.summary),
    [isKolInfoLocked, performanceSummary],
  )

  const { width } = useWindowSize()

  return (
    <KolDetailDataBlock
      colSpan={24}
      header={{
        poweredByAI: true,
        title: formatMessage(
          {
            id: 'kol:platform_top_3_performance_metric',
          },
          {
            platform: platform.title,
          },
        ),
      }}
      kolId={kolInfo?.id}
      kolMetrics='Rating Comparison'
      kolName={kolInfo?.name}
      platform={platform.shortcode}
    >
      <Flex gap={20} vertical={width < 981}>
        <ChartFlexWrapper justify='space-between'>
          <MetricChart
            chartType='interactiveRate'
            isKolInfoLocked={isKolInfoLocked}
            isLoading={isFetchingKolInfo}
            prRange={interactiveRatePerformance}
            showMockData={showMockData}
            value={interactiveRate ?? 0}
          />
          <MetricChart
            chartType='viewRate'
            isKolInfoLocked={isKolInfoLocked}
            isLoading={isFetchingKolInfo}
            prRange={viewRatePerformance}
            showMockData={showMockData}
            value={viewRate ?? 0}
          />
          <MetricChart
            chartType='fansUpRate'
            isKolInfoLocked={isKolInfoLocked}
            isLoading={isFetchingKolInfo}
            prRange={fansUpRatePerformance}
            showMockData={showMockData}
            value={fansUpRate ?? 0}
          />
        </ChartFlexWrapper>
        <DescriptionWrapper>
          {isFetchingKolInfo || isFetchingPerformanceSummary ? (
            <Skeleton active paragraph={{ rows: 2 }} />
          ) : (
            <DescriptionContent isLocked={hiddenData}>
              {summary ??
                formatMessage({
                  id: 'autoreport:chart_label_empty_data',
                })}
            </DescriptionContent>
          )}
          <PerformanceWrapper align='baseline' justify='flex-start'>
            {formatMessage({
              id: 'kol:platform_performance_level_instruction',
            })}
            <Tooltip
              title={formatMessage({
                id: 'kol:platform_performance_level_instruction_tooltip',
              })}
            >
              <IconWrapper>
                <Icon name='help-circle' />
              </IconWrapper>
            </Tooltip>
            {formatMessage({ id: 'general:colon' })}
            <Flex align='center' gap={5} wrap='wrap'>
              {Object.keys(levelMap).map((key) => {
                const level = levelMap[key]
                return (
                  <LevelItem
                    backgroundColor={level.scoreColor}
                    key={level.type}
                  >
                    {formatMessage({ id: level.metricLevelI18nId })}
                  </LevelItem>
                )
              })}
            </Flex>
          </PerformanceWrapper>
        </DescriptionWrapper>
      </Flex>
    </KolDetailDataBlock>
  )
}

const ChartFlexWrapper = styled(Flex)`
  flex: 1;
  position: relative;
  top: -25px;
`

const DescriptionWrapper = styled.div`
  flex: 1;
`

const IconWrapper = styled.div`
  margin-left: 5px;
`

const DescriptionContent = styled.div<{ isLocked: boolean }>`
  margin-bottom: 20px;
  font-size: 14px;
  font-weight: 400;
  text-align: left;
  line-height: 19.6px;
  color: ${({ theme }): string => theme.colors.base.grey.grey2};
  filter: blur(${({ isLocked }): string => (isLocked ? '5px' : '0')});
`

const PerformanceWrapper = styled(Flex)`
  font-size: 13px;
  font-weight: 400;
  text-align: left;
  color: ${({ theme }): string => theme.colors.base.grey.grey3};
  white-space: nowrap;
`

const LevelItem = styled.span<{ backgroundColor: string }>`
  position: relative;
  padding-left: 18px;
  font-size: 13px;
  font-weight: 400;
  text-align: left;
  color: ${({ theme }): string => theme.colors.base.grey.grey3};

  &:first-child {
    margin-left: 5px;
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 12px;
    height: 12px;
    border-radius: 2px;
    background-color: ${({ backgroundColor }): string => backgroundColor};
  }
`

export default PlatformMetricsSummary
